/**
 * Restrict height or/and width of an absolute positionned element to the limits of the viewport.
 * Assumes:
 * - One anchor point (left, right, top, bottom) that's within the viewport. (Not appropriate to bring inside the viewport an element that's anchored outside.)
 * - No property max-(height|width) or min-(height-width) already set inline.
 * @param {HTMLElement} target : Element to restrict.
 * @param {Array} propertiesToRestrict : Properties we want to restrict, default ['width', 'height']
 * @param {number} padding : Padding added to the calculation, default 20.
 */
export const restrictAbsoluteToViewport = (target: HTMLElement | null, propertiesToRestrict = ['width', 'height'], padding: number = 20): void => {
  if (target != null) {
    const { left, right, top, bottom } = target.getBoundingClientRect()
    const { innerHeight: viewportHeight, innerWidth: viewportWidth } = window

    const { uiLastHeight, uiLastWidth } = target.dataset
    if (uiLastHeight && uiLastWidth &&
      parseInt(uiLastHeight) === innerHeight && parseInt(uiLastWidth) === innerWidth) {
      return
    }

    // Reset style to redo calculations if new height or new width
    ['max-height', 'min-height', 'max-width', 'min-width'].forEach(p => target.style.removeProperty(p))

    target.dataset.uiLastWidth = innerWidth.toString()
    target.dataset.uiLastHeight = innerHeight.toString()

    if (propertiesToRestrict.includes('width') && (left < 0 || right > viewportWidth)) {
      assignMaxValue(target, 'Width', calculateMaxValue(left, right, viewportWidth, padding))
    } else if (propertiesToRestrict.includes('height') && (top < 0 || bottom > viewportHeight)) {
      assignMaxValue(target, 'Height', calculateMaxValue(top, bottom, viewportHeight, padding))
    }
  }
}

/**
 * Trigger restrictAbsoluteToViewport on an element using a Mutation Observer.
 * Uses attribute(s) if the argument is present, else observe changes in child list and subtree.
 * @param {HTMLElement} target: Element to resize.
 * @param {HTMLElement} elementToObserve : Element which changes triggers resizing (target is used if null).
 * @param {boolean} disconnectAfter : Disconnect observer after the first trigger (true by default).
 * @param {string[]} attributesToObserve : Attributes to observe on elementToObserve (if null, child list and subtree are observed).
 */
export const observeRestrictAbsoluteToViewport = (
  target: HTMLElement,
  elementToObserve: HTMLElement | Document | null = null,
  disconnectAfter: boolean = true,
  attributesToObserve: string[] | null = null,
  propertiesToRestrict: string[] = ['width', 'height']
): void => {
  const observer = new MutationObserver(() => {
    if (target.offsetParent != null) {
      restrictAbsoluteToViewport(target, propertiesToRestrict)
      if (disconnectAfter) observer.disconnect()
    }
  })

  const options: MutationObserverInit = (attributesToObserve != null) ? { attributeFilter: attributesToObserve } : { childList: true, subtree: true }
  observer.observe((elementToObserve ?? target), options)
}

/**
 * Get the max space available for an element according to viewport width or height
 * and top/bottom or left/right position of element.
 * @param {number} top: Value closest to 0 (left for width, top for height).
 * @param {number} bottom : Value closest to edge (right for width, bottom for height).
 * @param {number} viewportValue : Height or width of the viewport.
 * @param {number} padding : Padding added to the calculation.
 */
const calculateMaxValue = (top: number, bottom: number, viewportValue: number, padding: number): number =>
  viewportValue - (top < 0 ? 0 : top) - (bottom > viewportValue ? 0 : (viewportValue - bottom)) - padding

/**
 * Assign value to maxHeight or maxWidth property.
 * If minHeight or minWidth is higher, assign max value to the property
 * @param {HTMLElement} target: Element to style.
 * @param {string} propertyName : 'Height' or 'Width'.
 * @param {number} value : Max height/width value.
 */
const assignMaxValue = (target: HTMLElement, propertyName: 'Height' | 'Width', value: number): void => {
  target.style[`max${propertyName}`] = `${value}px`
  target.style[`min${propertyName}`] = `${Math.min(value, parseFloat(getComputedStyle(target)[`min${propertyName}`]))}px`
}
